import React, { useEffect, useState } from "react";
import { HashRouter, Route, Routes, Outlet, useLocation } from "react-router-dom";
import "./App.css";
import Noise from "./components/Noise.jsx";
import BufferLine from "./components/BufferLine/BufferLine.jsx";
import NeoTree from "./components/NeoTree/NeoTree.jsx";
import Editor from "./components/Editor/Editor.jsx";
import StatusLine from "./components/StatusLine/StatusLine.jsx";
import Dashboard from "./components/Dashboard/Dashboard.jsx";
import About from "./components/About/About.jsx";
import Projects from "./components/Projects/Projects.jsx";
import ProjectDetail from "./components/ProjectDetail/ProjectDetail.jsx";
import Resume from "./components/Resume/Resume.jsx";
import Contact from "./components/Contact/Contact.jsx";
import Experience from "./components/Experience/Experience.jsx";
import ExperienceDetail from "./components/ExperienceDetail/ExperienceDetail.jsx";
import useStore from "./lib/store";
import { getRouteBuffer } from "./lib/navigation";
import KeyboardHandler from "./components/KeyboardHandler/KeyboardHandler.jsx";
import Telescope from "./components/Telescope/Telescope.jsx";
import WhichKey from "./components/WhichKey/WhichKey.jsx";
import CommandMode from "./components/CommandMode/CommandMode.jsx";
import FloatingWindow from "./components/FloatingWindow/FloatingWindow.jsx";
import KeyboardHint from "./components/KeyboardHint/KeyboardHint.jsx";

/* ---------- Layout shell — wraps routes with the Neovim IDE frame ---------- */
function Layout() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const location = useLocation();
  const openBuffer = useStore((s) => s.openBuffer);
  const theme = useStore((s) => s.theme);

  // Apply theme to <html data-theme="...">
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Resize observer for the background canvas
  useEffect(() => {
    const handleResize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Keep the active buffer in sync with the current route
  useEffect(() => {
    openBuffer(getRouteBuffer(location.pathname));
  }, [location.pathname, openBuffer]);

  return (
    <div className="App">
      {/* Full-viewport p5.js noise background */}
      <div className="background-layer">
        <Noise width={size.width} height={size.height} theme={theme} />
      </div>

      {/* Floating Neovim-style terminal window */}
      <div className="terminal-wrapper">
        <BufferLine />
        <NeoTree />
        <Editor />
        <StatusLine />
      </div>

      {/* Interactive overlays & global keyboard handling */}
      <KeyboardHandler />
      <Telescope />
      <WhichKey />
      <CommandMode />
      <FloatingWindow />
      <KeyboardHint />
    </div>
  );
}

/* ---------- Root — HashRouter for GitHub Pages compatibility ---------- */
function App() {
  return (
    <HashRouter>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/about" element={<About />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:slug" element={<ProjectDetail />} />
          <Route path="/resume" element={<Resume />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/experience" element={<Experience />} />
          <Route path="/experience/:slug" element={<ExperienceDetail />} />
          <Route path="*" element={<Dashboard />} />
        </Route>
      </Routes>
    </HashRouter>
  );
}

export default App;